/**
 * Referrals tab for the Admin Module (PR #5d).
 *
 * Shows open quote referrals awaiting underwriter review.
 *
 * Access model:
 *   - Internal users with `quotes.bind` can see all referrals and
 *     may approve or decline each one.
 *   - Broker users with `quotes.refer` see only their own entity's
 *     referrals (read-only status view).
 *
 * Server state is held under TanStack Query key ['referrals'].
 * Approve/decline mutations invalidate that key on success.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuoteReferral } from '@cortex/domain';

import { useAuth } from '@/auth';
import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { useToast } from '@/components/ui/Toast';
import { CurrencyValue, formatMoney } from '@/components/ui/CurrencyValue';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { REFERRAL_STATUS_LABELS, toneForReferralStatus } from '@/components/ui/referralStatus';

const REFERRALS_KEY = ['referrals'] as const;

const statusBadge = (status: QuoteReferral['status']): React.ReactNode => (
  <StatusBadge tone={toneForReferralStatus(status)} label={REFERRAL_STATUS_LABELS[status]} />
);

export function ReferralsTab() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const canAct = hasPermission('quotes.bind');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [declineNotes, setDeclineNotes] = useState('');
  const [actionPanel, setActionPanel] = useState<'approve' | 'decline' | null>(null);

  const referralsQuery = useQuery({
    queryKey: REFERRALS_KEY,
    queryFn: () => cortexApi.quotes.listReferrals(),
  });

  const referrals: readonly QuoteReferral[] = referralsQuery.data?.referrals ?? [];
  const selected = referrals.find((r) => r.id === selectedId) ?? null;

  const approveMutation = useMutation({
    mutationFn: (id: string) => cortexApi.quotes.approveReferral(id, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: REFERRALS_KEY });
      showToast('Referral approved.', 'success');
      setActionPanel(null);
    },
    onError: (err) => {
      const { title, message } = resolveApiError(err);
      showToast(`${title}: ${message}`, 'error');
    },
  });

  const declineMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      cortexApi.quotes.declineReferral(id, { notes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: REFERRALS_KEY });
      showToast('Referral declined.', 'success');
      setActionPanel(null);
      setDeclineNotes('');
    },
    onError: (err) => {
      const { title, message } = resolveApiError(err);
      showToast(`${title}: ${message}`, 'error');
    },
  });

  const isBusy = approveMutation.isPending || declineMutation.isPending;

  return (
    <div className="flex h-full gap-4">
      {/* ── List pane ────────────────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm min-h-0 w-full lg:w-[380px] lg:flex-shrink-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Referrals</h2>
          {referralsQuery.isFetching && (
            <span className="text-xs text-muted-foreground">Refreshing…</span>
          )}
        </div>

        {referralsQuery.isLoading ? (
          <div className="flex flex-1 items-center justify-center p-8 text-xs text-muted-foreground">
            Loading…
          </div>
        ) : referrals.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-8 text-xs text-muted-foreground">
            No referrals yet.
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto divide-y divide-border">
            {referrals.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(r.id);
                    setActionPanel(null);
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors ${
                    r.id === selectedId ? 'bg-accent' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs font-bold text-foreground truncate">
                      {r.quoteRef}
                    </span>
                    {statusBadge(r.status)}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <CurrencyValue amount={r.grossPremium} />
                    <span>
                      {new Date(r.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Detail pane ──────────────────────────────────────────────── */}
      <div className="hidden lg:flex flex-col flex-1 overflow-hidden rounded-lg border border-border bg-card shadow-sm min-h-0">
        {selected === null ? (
          <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
            Select a referral to view details.
          </div>
        ) : (
          <div className="flex flex-col h-full overflow-y-auto p-6 gap-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-bold text-foreground font-mono">
                  {selected.quoteRef}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">Referral ID: {selected.id}</p>
              </div>
              {statusBadge(selected.status)}
            </div>

            <dl className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <dt className="font-bold text-muted-foreground uppercase tracking-widest text-micro mb-0.5">
                  Gross premium
                </dt>
                <dd className="font-bold text-foreground text-sm">
                  <CurrencyValue amount={selected.grossPremium} />
                </dd>
              </div>
              <div>
                <dt className="font-bold text-muted-foreground uppercase tracking-widest text-micro mb-0.5">
                  Submitted
                </dt>
                <dd className="text-foreground">
                  {new Date(selected.createdAt).toLocaleString('en-GB', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </dd>
              </div>
              {selected.reviewedAt && (
                <>
                  <div>
                    <dt className="font-bold text-muted-foreground uppercase tracking-widest text-micro mb-0.5">
                      Reviewed at
                    </dt>
                    <dd className="text-foreground">
                      {new Date(selected.reviewedAt).toLocaleString('en-GB', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </dd>
                  </div>
                  {selected.reviewerNotes && (
                    <div className="col-span-2">
                      <dt className="font-bold text-muted-foreground uppercase tracking-widest text-micro mb-0.5">
                        Reviewer notes
                      </dt>
                      <dd className="text-foreground">{selected.reviewerNotes}</dd>
                    </div>
                  )}
                </>
              )}
            </dl>

            {/* Action buttons — only for internal users + open referrals */}
            {canAct && selected.status === 'open' && (
              <div className="mt-auto space-y-3">
                {actionPanel === null && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setActionPanel('approve')}
                      className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => setActionPanel('decline')}
                      className="flex-1 rounded-md border border-red-300 bg-white px-4 py-2 text-xs font-bold text-red-600 uppercase tracking-widest hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      Decline
                    </button>
                  </div>
                )}

                {actionPanel === 'approve' && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                    <p className="text-xs font-semibold text-emerald-800">
                      Confirm approval for {formatMoney(selected.grossPremium)}?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => approveMutation.mutate(selected.id)}
                        disabled={isBusy}
                        className="flex-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                      >
                        {approveMutation.isPending ? 'Approving…' : 'Confirm approve'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActionPanel(null)}
                        disabled={isBusy}
                        className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {actionPanel === 'decline' && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
                    <label
                      className="block text-xs font-semibold text-red-800"
                      htmlFor="decline-notes"
                    >
                      Reason for declining (required)
                    </label>
                    <textarea
                      id="decline-notes"
                      value={declineNotes}
                      onChange={(e) => setDeclineNotes(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-red-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-400"
                      placeholder="Briefly explain why this referral is declined…"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          declineMutation.mutate({ id: selected.id, notes: declineNotes })
                        }
                        disabled={isBusy || declineNotes.trim().length === 0}
                        className="flex-1 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
                      >
                        {declineMutation.isPending ? 'Declining…' : 'Confirm decline'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActionPanel(null);
                          setDeclineNotes('');
                        }}
                        disabled={isBusy}
                        className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
